/** Signals — triage list view (server-side pagination/sort/search + delete). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import { Icon } from "@/components/ui/icon";
import { SIGNAL_STATUS, type Signal } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/app/StatusBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";

import {
  DataTable,
  PageHeader,
  SearchBox,
  type RefineTableCore,
} from "../projects/_shared";
import { humanize } from "../projects/_form";

export function SignalList() {
  const go = useGo();
  const { editUrl, showUrl, createUrl } = useNavigation();
  const { mutate: deleteSignal } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState<Signal | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<string>("");

  const columns = useMemo<ColumnDef<Signal, unknown>[]>(
    () => [
      {
        id: "title",
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.title}</span>
        ),
      },
      {
        id: "signal_type",
        accessorKey: "signal_type",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant="secondary">
            {humanize(row.original.signal_type)}
          </Badge>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "strength",
        accessorKey: "strength",
        header: "Strength",
        cell: ({ row }) =>
          row.original.strength != null ? (
            <span className="font-mono text-xs tabular-nums">
              {row.original.strength.toFixed(2)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {new Date(row.original.created_at).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onEdit={() => go({ to: editUrl("signals", row.original.id) })}
            onShow={() => go({ to: showUrl("signals", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  const table = useTable<Signal, HttpError>({
    columns,
    refineCoreProps: {
      resource: "signals",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: {
        field: string;
        operator: "contains" | "eq";
        value: string;
      }[],
    ) => void;
  };
  const [search, setSearch] = React.useState("");

  // Combine the `q` search and the `status` equality filter into a single
  // setFilters call (the data provider maps `q`→search, `eq`→`status=<value>`).
  React.useEffect(() => {
    const handle = setTimeout(() => {
      const filters: {
        field: string;
        operator: "contains" | "eq";
        value: string;
      }[] = [{ field: "q", operator: "contains", value: search }];
      if (statusFilter) {
        filters.push({ field: "status", operator: "eq", value: statusFilter });
      }
      core.setFilters(filters);
    }, 300);
    return () => clearTimeout(handle);
  }, [search, statusFilter, core]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Signals"
        description="Pains, anomalies, opportunities, and risks awaiting triage."
        action={
          <Button onClick={() => go({ to: createUrl("signals") })}>
            <Icon name="create" size={16} className="mr-2" aria-hidden /> New
            Signal
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search signals…"
        />
        <Select
          value={statusFilter || "__all__"}
          onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            {SIGNAL_STATUS.map((s) => (
              <SelectItem key={s} value={s}>
                {humanize(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No signals recorded yet."
        onRowClick={(s) => go({ to: showUrl("signals", s.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title="Delete signal?"
        description={
          <>
            This removes{" "}
            <span className="font-medium">{pendingDelete?.title}</span>. This
            action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deleteSignal({ resource: "signals", id: pendingDelete.id });
          }
        }}
      />
    </div>
  );
}

function RowActions({
  onEdit,
  onShow,
  onDelete,
}: {
  onEdit: () => void;
  onShow: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Icon name="more" size={16} aria-hidden />
          <span className="sr-only">Open actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShow}>View</DropdownMenuItem>
        <DropdownMenuItem onClick={onEdit}>
          <Icon name="edit" size={16} className="mr-2" aria-hidden /> Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Icon name="delete" size={16} className="mr-2" aria-hidden /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default SignalList;
